import { ISelectOption } from './form';
import { IPageOption } from './ui';

export type ApplicationBaseType =
  | 'GENERAL' // 일반
  | 'STAFF' // 운영진
  | 'MEDICAL' // 의료
  | 'EDUCATION' // 교육
  | 'PHOTOGRAPHY' // 사진
  | 'DRIVING' // 운전
  | 'ART' // 미술
  | 'ETC' // 기타
  | 'SYSTEM_GENERAL'; // 일반(시스템추가)

export type ApplicationType =
  | ApplicationBaseType
  | `${ApplicationBaseType}_${number}`;

export type RecruitmentType = 'FCFS' | 'LOTTERY';

export const APPLICATION_SELECT: ISelectOption<ApplicationBaseType>[] = [
  { name: '미술', value: 'ART' },
  { name: '교육', value: 'EDUCATION' },
  { name: '의료', value: 'MEDICAL' },
  { name: '운전', value: 'DRIVING' },
];

export const APPLICATION_TICKET_SELECT: ISelectOption<ApplicationBaseType>[] = [
  { name: '일반', value: 'GENERAL' },
  ...APPLICATION_SELECT,
];

export const ADMIN_APPLICATION_SELECT: ISelectOption<ApplicationBaseType>[] = [
  { name: '운영진', value: 'STAFF' },
  { name: '사진', value: 'PHOTOGRAPHY' },
  { name: '일반(시스템추가)', value: 'SYSTEM_GENERAL' },
  { name: '기타', value: 'ETC' },
];

export const APPLICATION_TYPE_MAP: Record<ApplicationBaseType, string> = {
  DRIVING: '운전',
  ART: '미술',
  EDUCATION: '교육',
  MEDICAL: '의료',
  GENERAL: '일반',
  STAFF: '운영진',
  PHOTOGRAPHY: '사진',
  ETC: '기타',
  SYSTEM_GENERAL: '일반(시스템추가)',
};

/**
 * 유형 키에서 의미 축만 뽑는다. `DRIVING_2` → `DRIVING`, `DRIVING` → `DRIVING`
 */
export const getApplicationBaseType = (key: string) => key.replace(/_\d+$/, '');

export const isAdminApplicationType = (key: string | null) =>
  !!key &&
  ADMIN_APPLICATION_SELECT.some(
    ({ value }) => value === getApplicationBaseType(key),
  );

const APPLICATION_BASE_ORDER: ApplicationBaseType[] = [
  ...APPLICATION_TICKET_SELECT.map((option) => option.value),
  ...ADMIN_APPLICATION_SELECT.map((option) => option.value),
];

export const getApplicationBaseOrder = (key: string) => {
  const index = APPLICATION_BASE_ORDER.indexOf(
    getApplicationBaseType(key) as ApplicationBaseType,
  );

  return index === -1 ? APPLICATION_BASE_ORDER.length : index;
};

export const getApplicationTypeSeq = (key: string) => {
  const seq = key.match(/_(\d+)$/);

  return seq ? Number(seq[1]) : 1;
};

export const compareApplicationTypes = (a: string, b: string) => {
  const order = getApplicationBaseOrder(a) - getApplicationBaseOrder(b);
  if (order !== 0) return order;

  return getApplicationTypeSeq(a) - getApplicationTypeSeq(b);
};

export const buildApplicationTypeKeys = (
  bases: Array<ApplicationBaseType>,
): ApplicationType[] => {
  const seen: Record<string, number> = {};

  return bases.map((base) => {
    seen[base] = (seen[base] ?? 0) + 1;

    return (
      seen[base] === 1 ? base : `${base}_${seen[base]}`
    ) as ApplicationType;
  });
};

/**
 * 화면에 노출할 신청 유형 이름
 */
export const getApplicationTypeLabel = (
  applicationType?: string | null,
  titleMap?: Record<string, string> | null,
  fallback = '-',
) => {
  if (!applicationType) return fallback;

  return (
    titleMap?.[applicationType] ||
    APPLICATION_TYPE_MAP[
      getApplicationBaseType(applicationType) as ApplicationBaseType
    ] ||
    applicationType
  );
};

export const getApplicationTypeDetailLabel = (
  applicationType?: string | null,
  titleMap?: Record<string, string> | null,
  fallback = '-',
) => {
  if (!applicationType) return fallback;

  const base = getApplicationTypeLabel(getApplicationBaseType(applicationType));
  const title = titleMap?.[applicationType]?.trim();

  return title ? `${base}(${title})` : base;
};

export interface IEvent {
  id: number;
  eventStatus:
    | 'RECRUITING'
    | 'REVIEW'
    | 'RECRUITMENT_CLOSED'
    | 'COMPLETED'
    | 'CANCELLED';
  creator: string;
  createdAt: string;
  mainImageUrl?: string;
  title: string;
  startDate: string;
  endDate: string;
  enrollStartDate: string;
  enrollEndDate: string;
  latitude: number;
  longitude: number;
  centerLatitude?: number;
  centerLongitude?: number;
  locationName: string;
  organizationId?: number;
  organizationName: string;
  organizationSortOrder?: { organizationId: number; sortOrder: number }[];
  participantCount: number;
  recruitmentType?: RecruitmentType;
  certificationAgency: CAType[];
  description?: string;
  notice?: string;
  volunteerHours: number;
  openTalkId?: number | null;

  additionalId?: number | null;
  feedbackId?: number | null;

  gatheringPlace?: string;
  feedbackGuide?: string;
  contentSortOrder?: {
    contentId: number;
    sortOrder: number;
  }[];

  applicationTypeQuota?: Record<string, number> | null;
  applicationTypeQuotaTitle?: Record<string, string> | null;
}

export type CAType = 'VMS' | '1365';

export type EventCreateType = Pick<
  IEvent,
  | 'mainImageUrl'
  | 'title'
  | 'startDate'
  | 'endDate'
  | 'enrollStartDate'
  | 'enrollEndDate'
  | 'latitude'
  | 'longitude'
  | 'centerLatitude'
  | 'centerLongitude'
  | 'locationName'
  | 'organizationName'
  | 'participantCount'
  | 'recruitmentType'
  | 'certificationAgency'
  | 'volunteerHours'
  | 'description'
  | 'gatheringPlace'
  | 'applicationTypeQuota'
  | 'applicationTypeQuotaTitle'
> & {
  organizationIds?: number[];
};

export type EventUpdateType = Partial<IEvent> & { isNewFile: boolean };

export type EventItemType = Pick<
  IEvent,
  | 'id'
  | 'title'
  | 'organizationName'
  | 'eventStatus'
  | 'createdAt'
  | 'startDate'
  | 'endDate'
  | 'enrollStartDate'
  | 'enrollEndDate'
>;

export type SearchEventFilterType = Partial<Pick<IEvent, 'title'>>;
export type SearchEventQueryType = IPageOption & SearchEventFilterType;
